/**
 * Container for Bio
 *
 * @module components/Bio
 */
import values from 'lodash/values';
import PropTypes from 'prop-types';
import React from 'react';
import ReactDOM from 'react-dom';
import { FormattedMessage } from 'react-intl';

import { messages as m } from '../../constants/Bio';
import QA from '../../constants/QATags';
import { getBackstageLocation } from '../../lib/RouteUtils';
import { parseDescriptionBackstageStringToReactComponent } from '../../lib/StringUtils';
import { jeCSS } from '../../lib/StyleUtils';
import { addWindowEventListener, removeWindowEventListener } from '../../tools/Globals';

import './Bio.scss';

export const styleNames = {
  thinTitle: 'thinTitle',
  squareImage: 'squareImage',
};
export const styleNameList = values(styleNames);

class Bio extends React.Component {
  static propTypes = {
    imgUrl: PropTypes.string,
    label: PropTypes.object,
    styleNames: PropTypes.arrayOf(PropTypes.oneOf(styleNameList)),
    text: PropTypes.string.isRequired,
    titleName: PropTypes.string,
  };

  state = {
    isCutoff: false,
    isExpanded: false,
    focused: false,
  };

  componentDidMount() {
    // using setTimeout to ensure that we have already loaded CSS
    setTimeout(this.updateIsCutoff, 0);
    addWindowEventListener('resize', this.recalculateLayout);
  }

  componentDidUpdate(nextProps, nextState) {
    // set bioText div height to height of contents so animation is performed
    this.updateTextHeight();
    if (nextState.text !== this.state.text) {
      this.updateIsCutoff();
    }
  }

  componentWillUnmount() {
    removeWindowEventListener('resize', this.recalculateLayout);
  }

  recalculateLayout = () => {
    this.updateIsCutoff();
    this.updateTextHeight();
  };

  updateTextHeight = () => {
    if (this.state.isExpanded) {
      this.refs.bioText.style.maxHeight = this.refs.bioTextInnerWrapper.offsetHeight + 'px';
    } else {
      this.refs.bioText.style.maxHeight = '';
    }
  };

  updateIsCutoff = () => {
    // eslint-disable-next-line react/no-find-dom-node
    const node = ReactDOM.findDOMNode(this.refs.bioText);
    if (node && !this.state.isExpanded) {
      this.setState({ isCutoff: node.offsetHeight < node.scrollHeight });
    }
  };

  renderImage = hasImageUrl => {
    if (hasImageUrl) {
      const artistImgStyle = {
        backgroundImage: `url('${this.props.imgUrl}')`,
      };
      return <div className='Bio__image' style={artistImgStyle}></div>;
    }
  };

  renderDivider = () => {
    if (this.state.isCutoff) {
      return (
        <div className='Bio__divider'>
          <div
            className='Bio__divider__button'
            onClick={this.toggleShowMore}
            data-qa={QA.BSMB}
            role='button'
          >
            {this.state.isExpanded ? (
              <FormattedMessage {...m.showLess} />
            ) : (
              <FormattedMessage {...m.showMore} />
            )}
          </div>
        </div>
      );
    }
    return <div className='Bio__divider Bio__divider--noline' />;
  };

  toggleShowMore = () => {
    this.setState({
      isExpanded: !this.state.isExpanded,
    });
  };

  render() {
    const label = this.props.label ? this.props.label : m.about;
    const bioTextClasses = jeCSS('Bio__text', {
      expanded: this.state.isExpanded,
      noImg: !this.props.imgUrl,
    });

    return (
      <div className={jeCSS('Bio', this.props.styleNames)}>
        <h2 className='Bio__title' data-qa={QA.BT}>
          <FormattedMessage {...label} /> {this.props.titleName}
        </h2>
        {this.renderImage(this.props.imgUrl)}
        <div className={bioTextClasses} ref='bioText' data-qa={QA.BTX}>
          <div className='Bio__text__innerWrapper' ref='bioTextInnerWrapper'>
            {parseDescriptionBackstageStringToReactComponent(this.props.text, getBackstageLocation)}
          </div>
        </div>
        {this.renderDivider()}
      </div>
    );
  }
}

export default Bio;
